import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Inbox, Mail, MapPin, ArrowLeft, Phone, Lock, LogOut, Search, CreditCard } from 'lucide-react';
import { listEnquiries, updateEnquiryStatus, adminLogin, getAdminToken, setAdminToken, clearAdminToken, listPayments } from '../lib/api';

const STATUSES = [
  { key: 'new', label: 'New', badge: 'bg-blue-100 text-blue-700', btn: 'bg-blue-600' },
  { key: 'contacted', label: 'Contacted', badge: 'bg-amber-100 text-amber-700', btn: 'bg-amber-500' },
  { key: 'closed', label: 'Closed', badge: 'bg-emerald-100 text-emerald-700', btn: 'bg-emerald-600' },
];
const CATEGORIES = ['Flight', 'Hotel', 'Package', 'Visa', 'SightSeeing', 'Miscellaneous', 'Transfer'];

function LoginGate({ onSuccess }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { token } = await adminLogin(password);
      setAdminToken(token);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B2545] grid place-items-center px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
        <span className="grid place-items-center h-14 w-14 mx-auto rounded-2xl bg-[#EA5019]/10 text-[#EA5019]"><Lock size={26} /></span>
        <h1 className="mt-5 text-center font-heading text-2xl font-extrabold text-[#0B2545]">Team Login</h1>
        <p className="mt-1.5 text-center text-sm text-slate-500">Enter the team password to view enquiries.</p>
        <input
          type="password"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          autoFocus
          className="mt-6 w-full rounded-xl border border-slate-300 px-4 py-3 text-[#0B2545] outline-none focus:border-[#EA5019] focus:ring-2 focus:ring-[#EA5019]/15 transition"
        />
        {error && <p data-testid="admin-login-error" className="mt-2 text-sm text-red-500">{error}</p>}
        <button type="submit" disabled={busy || !password} data-testid="admin-login-submit" className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white bg-[#EA5019] hover:bg-[#cf4413] transition-all shadow-[0_10px_26px_rgba(234,80,25,0.35)] ${busy || !password ? 'opacity-60 cursor-not-allowed' : ''}`}>
          {busy ? 'Signing in…' : 'Sign In'}
        </button>
        <Link to="/" className="mt-5 block text-center text-sm text-slate-400 hover:text-slate-600">← Back to site</Link>
      </form>
    </div>
  );
}

export default function AdminEnquiries() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [tab, setTab] = useState('enquiries');
  const [payments, setPayments] = useState([]);
  const [loadingPay, setLoadingPay] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (category) params.category = category;
      if (status) params.status = status;
      setRows(await listEnquiries(params));
    } catch (e) {
      if (e.response?.status === 401) { clearAdminToken(); setAuthed(false); }
      setRows([]);
    }
    setLoading(false);
  }, [q, category, status]);

  const loadPay = useCallback(async () => {
    setLoadingPay(true);
    try {
      setPayments(await listPayments());
    } catch (e) {
      if (e.response?.status === 401) { clearAdminToken(); setAuthed(false); }
      setPayments([]);
    }
    setLoadingPay(false);
  }, []);

  useEffect(() => {
    if (!authed || tab !== 'enquiries') return;
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [authed, tab, load]);

  useEffect(() => {
    if (authed && tab === 'payments') loadPay();
  }, [authed, tab, loadPay]);

  const setEnquiryStatus = async (id, newStatus) => {
    setUpdating(id + newStatus);
    try {
      const updated = await updateEnquiryStatus(id, newStatus);
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status: updated.status } : r)));
    } catch (e) {
      if (e.response?.status === 401) { clearAdminToken(); setAuthed(false); }
    }
    setUpdating(null);
  };

  const logout = () => { clearAdminToken(); setAuthed(false); };

  if (!authed) return <LoginGate onSuccess={() => setAuthed(true)} />;

  const selectCls = 'rounded-full bg-white/10 border border-white/20 text-white text-sm px-4 py-2 outline-none focus:border-white/50 [&>option]:text-[#0B2545]';

  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      <div className="bg-[#0B2545] text-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-3"><ArrowLeft size={16} /> Back to site</Link>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-extrabold flex items-center gap-3">
                {tab === 'enquiries' ? <><Inbox size={28} className="text-[#ff7a45]" /> Enquiries</> : <><CreditCard size={28} className="text-[#ff7a45]" /> Payments</>}
              </h1>
              <p className="text-white/60 text-sm mt-1">{tab === 'enquiries' ? `${rows.length} result${rows.length !== 1 ? 's' : ''}` : `${payments.length} transaction${payments.length !== 1 ? 's' : ''}`}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => (tab === 'enquiries' ? load() : loadPay())} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-sm font-semibold transition"><RefreshCw size={15} className={(loading || loadingPay) ? 'animate-spin' : ''} /> Refresh</button>
              <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-sm font-semibold transition"><LogOut size={15} /> Logout</button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex items-center gap-2">
            <button onClick={() => setTab('enquiries')} data-testid="admin-tab-enquiries" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition ${tab === 'enquiries' ? 'bg-[#EA5019] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}><Inbox size={15} /> Enquiries</button>
            <button onClick={() => setTab('payments')} data-testid="admin-tab-payments" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition ${tab === 'payments' ? 'bg-[#EA5019] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}><CreditCard size={15} /> Payments</button>
          </div>

          {/* Search + filters (enquiries only) */}
          {tab === 'enquiries' && (
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/50" />
              <input
                data-testid="admin-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email or destination…"
                className="w-full rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 text-sm pl-10 pr-4 py-2 outline-none focus:border-white/50"
              />
            </div>
            <select data-testid="admin-filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className={selectCls}>
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="admin-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
              <option value="">All Statuses</option>
              {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        {tab === 'payments' ? (
          loadingPay ? (
            <p className="text-slate-500">Loading…</p>
          ) : payments.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center text-slate-500 shadow-sm" data-testid="admin-payments-empty">No payment transactions yet.</div>
          ) : (
            <div className="overflow-x-auto bg-white rounded-2xl shadow-[0_10px_26px_rgba(11,37,69,0.06)]" data-testid="admin-payments-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-100">
                    <th className="px-5 py-3 font-semibold">Date</th>
                    <th className="px-5 py-3 font-semibold">Customer</th>
                    <th className="px-5 py-3 font-semibold">Item</th>
                    <th className="px-5 py-3 font-semibold text-right">Amount</th>
                    <th className="px-5 py-3 font-semibold">Payment ID</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const st = p.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : p.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700';
                    return (
                      <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`payment-row-${p.id}`}>
                        <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{p.created_at ? new Date(p.created_at).toLocaleString('en-IN') : '—'}</td>
                        <td className="px-5 py-3">
                          <div className="font-medium text-[#0B2545]">{p.name || '—'}</div>
                          <div className="text-slate-400 text-xs">{p.email || ''} {p.phone || ''}</div>
                        </td>
                        <td className="px-5 py-3 text-slate-600 max-w-[240px]">{p.package || '—'}</td>
                        <td className="px-5 py-3 text-right font-semibold text-[#0B2545] whitespace-nowrap">₹{Number(p.amount || 0).toLocaleString('en-IN')}</td>
                        <td className="px-5 py-3 text-slate-500 text-xs">{p.payment_id || '—'}</td>
                        <td className="px-5 py-3"><span className={`text-xs px-2.5 py-1 rounded-full font-semibold capitalize ${st}`}>{p.status || 'created'}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : loading ? (
          <p className="text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center text-slate-500 shadow-sm" data-testid="admin-empty-state">No enquiries match your filters.</div>
        ) : (
          <div className="grid gap-4">
            {rows.map((r) => {
              const details = [
                ['Trip Type', r.trip_type],
                ['Source', r.source_city],
                ['To', r.destination_city],
                ['Departure', r.departure],
                ['Return', r.return_date],
                ['Check In', r.check_in],
                ['Check Out', r.check_out],
                ['Travel Date', r.travel_date],
                ['Nationality', r.nationality],
                ['Pax', r.pax],
              ].filter(([, v]) => v);
              const current = r.status || 'new';
              const curMeta = STATUSES.find((s) => s.key === current) || STATUSES[0];
              return (
              <div key={r.id} className="bg-white rounded-2xl p-5 shadow-[0_10px_26px_rgba(11,37,69,0.06)]">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <h3 className="font-heading font-bold text-[#0B2545]">{r.name}</h3>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${curMeta.badge}`} data-testid={`enquiry-status-badge-${r.id}`}>{curMeta.label}</span>
                    {r.category && <span className="text-xs px-2.5 py-1 rounded-full bg-[#0B2545]/10 text-[#0B2545] font-semibold">{r.category}</span>}
                    <span className="text-xs px-2.5 py-1 rounded-full bg-[#EA5019]/10 text-[#EA5019] font-semibold capitalize">{r.source || 'contact'}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600 mb-2">
                  <span className="flex items-center gap-1.5"><Mail size={14} className="text-[#EA5019]" /> {r.email}</span>
                  {r.phone && <span className="flex items-center gap-1.5"><Phone size={14} className="text-[#EA5019]" /> {r.phone}</span>}
                  {r.destination && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[#EA5019]" /> {r.destination}</span>}
                </div>
                {details.length > 0 && (
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500 mb-2">
                    {details.map(([k, v]) => (
                      <span key={k}><span className="text-slate-400">{k}:</span> <span className="font-medium text-slate-600">{v}</span></span>
                    ))}
                  </div>
                )}
                {r.message && <p className="text-sm text-slate-500">{r.message}</p>}
                <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    {STATUSES.map((s) => {
                      const active = s.key === current;
                      return (
                        <button
                          key={s.key}
                          onClick={() => setEnquiryStatus(r.id, s.key)}
                          disabled={active || updating === r.id + s.key}
                          data-testid={`enquiry-status-btn-${s.key}-${r.id}`}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${active ? `${s.btn} text-white cursor-default` : 'bg-slate-100 text-slate-500 hover:bg-slate-200'} ${updating === r.id + s.key ? 'opacity-60' : ''}`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString('en-IN')}</p>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
